import React, { useState } from 'react';
import { Row, Col } from 'antd';
import { FormikProps, Formik } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import * as Yup from 'yup';

import Radio from 'components/formik/Radio';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import FormikEffect from 'components/formik/FormikEffect';
import {
  InvoicingCycleEnum,
  PayPeriodEnum,
  ResourceDownloadResponse,
  CommissionTypeEnum,
  PaymentTermEnum,
  CustomerCostingMethodTypeEnum,
} from 'api/backofficeApiClient';
import {
  getCommissionTypeApprovalLabel,
  getCustomerInvoicingCycleOptions,
  getCustomerPaymentTermsOptions,
  getCustomerPayrollCycleOptions,
} from 'utils/clientUtils';
import { urlRegex } from 'utils/urlUtils';
import {
  normalCharactersLength,
  mediumCharactersLength,
} from 'constants/index';
import TempToPermConversionUploader from './TempToPermConversionUploader/TempToPermConversionUploader';
import PurchaseOrderUploader from './PurchaseOrderUploader';
import { getCustomerCostingMethodTypeLabel } from 'utils/clientUtils';

import styles from '../GeneralContractInfo/GeneralContractInfo.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<FinancialInfoFormValues>) => void;
  initialValues?: FinancialInfoFormValues;
  tempToPermConversionFileInfo?: ResourceDownloadResponse;
  purchaseOrderFileInfo?: ResourceDownloadResponse;
};

export interface FinancialInfoFormValues {
  markupRate: string;
  dtBillRateMultiplier?: number | null;
  otRateMultiplier: string;
  conversion?: string[];
  MSPFee?: number | null | '';
  VMSFee?: number | null | '';
  invoicingInfo: string;
  invoicingInstructions: string;
  invoicingCycle: InvoicingCycleEnum | null;
  payrollCycle: PayPeriodEnum | null;
  isThirdWorkforce?: boolean;
  url?: string;
  credentials?: string;
  providerName?: string;
  creditCheck: string;
  commissionType: CommissionTypeEnum | null;
  notes: string;
  paymentTerms: null | PaymentTermEnum;
  purchaseOrder?: boolean;
  purchaseOrderFileKey: string[];
  purchaseOrderInstructions?: string;
  customerCostingMethodType: CustomerCostingMethodTypeEnum | null;
}

const validationSchema = Yup.object({
  markupRate: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Fixed markup rate should be ${mediumCharactersLength} characters maximum`,
    ),
  dtBillRateMultiplier: Yup.number()
    .nullable()
    .min(0, 'DT bill rate multiplier fee should be from 0 to 100')
    .max(100, 'DT bill rate multiplier fee should be from 0 to 100'),
  otRateMultiplier: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `OT Rate multiplier should be ${mediumCharactersLength} characters maximum`,
    ),
  conversion: Yup.array().of(Yup.string()),
  MSPFee: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value,
    )
    .min(0, 'MSP fee should be from 0 to 100')
    .max(100, 'MSP fee should be from 0 to 100'),
  VMSFee: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value,
    )
    .min(0, 'VMS fee should be from 0 to 100')
    .max(100, 'VMS fee should be from 0 to 100'),
  invoicingInfo: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Invoicing information should be ${mediumCharactersLength} characters maximum`,
    ),
  invoicingInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Special invoicing instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  invoicingCycle: Yup.number().nullable(),
  payrollCycle: Yup.number().nullable(),
  isThirdWorkforce: Yup.boolean(),
  url: Yup.string()
    .matches(urlRegex, 'URL format is invalid')
    .max(
      mediumCharactersLength,
      `Url should be ${mediumCharactersLength} characters maximum`,
    ),
  credentials: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Credentials should be ${normalCharactersLength} characters maximum`,
    ),
  providerName: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Provider name should be ${normalCharactersLength} characters maximum`,
    ),
  creditCheck: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Credit check score should be ${mediumCharactersLength} characters maximum`,
    ),
  commissionType: Yup.number().nullable(),
  notes: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Added notes should be ${mediumCharactersLength} characters maximum`,
    ),
  paymentTerms: Yup.number().nullable(),
  purchaseOrder: Yup.boolean(),
  purchaseOrderFileKey: Yup.array().of(Yup.string()),
  purchaseOrderInstructions: Yup.string().max(
    mediumCharactersLength,
    `Purchase order instructions should be ${mediumCharactersLength} characters maximum`,
  ),
  customerCostingMethodType: Yup.number().nullable(),
});

export default function FinancialInfo(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    onChange,
    tempToPermConversionFileInfo,
    purchaseOrderFileInfo,
  } = props;
  const [purchaseOrderFile, setPurchaseOrderFile] = useState(
    purchaseOrderFileInfo,
  );

  const initialValues: FinancialInfoFormValues = {
    markupRate: '',
    dtBillRateMultiplier: null,
    otRateMultiplier: '',
    conversion: [],
    MSPFee: undefined,
    VMSFee: undefined,
    invoicingInfo: '',
    invoicingInstructions: '',
    invoicingCycle: null,
    payrollCycle: null,
    isThirdWorkforce: undefined,
    url: undefined,
    credentials: undefined,
    providerName: undefined,
    creditCheck: '',
    commissionType: null,
    notes: '',
    paymentTerms: null,
    purchaseOrder: undefined,
    purchaseOrderFileKey: [],
    purchaseOrderInstructions: '',
    customerCostingMethodType: null,
  };

  const handleIsThirdWorkforce = (
    event: RadioChangeEvent,
    formik: FormikProps<FinancialInfoFormValues>,
  ) => {
    if (!event.target.value) {
      formik.setFieldValue('url', '', false);
      formik.setFieldValue('credentials', '', false);
      formik.setFieldValue('providerName', '', false);

      formik.setFieldTouched('url', false);
      formik.setFieldTouched('credentials', false);
      formik.setFieldTouched('providerName', false);
    }
  };

  const handlePurchaseOrderChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<FinancialInfoFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('purchaseOrderFileKey', '', false);
      formik.setFieldValue('purchaseOrderInstructions', '', false);
    }

    setPurchaseOrderFile(undefined);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Financial overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="creditCheck"
                  label="Credit check score"
                  data-id="editCustomerInfoCreditCheckField"
                >
                  <Input
                    name="creditCheck"
                    placeholder="Enter credit check score"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  label="Commission type approval"
                  data-id="editCustomerInfoCommissionTypeField"
                  name="commissionType"
                >
                  <Radio.Group name="commissionType">
                    <Radio
                      name="selfFundedRadio"
                      value={CommissionTypeEnum.SelfFunded}
                      className={styles.radio}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.SelfFunded,
                      )}
                    </Radio>

                    <Radio
                      name="paidWhenPaidRadio"
                      value={CommissionTypeEnum.PaidWhenPaid}
                      className={styles.radio}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.PaidWhenPaid,
                      )}
                    </Radio>

                    <Radio
                      name="advancedCommissionRadio"
                      value={CommissionTypeEnum.AdvancedCommission}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.AdvancedCommission,
                      )}
                    </Radio>
                  </Radio.Group>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="notes"
                  label="Added notes"
                  data-id="editCustomerInfoNotesField"
                >
                  <Input
                    name="notes"
                    placeholder="Enter added notes"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem name="paymentTerms" label="Payment terms">
                  <Select
                    name="paymentTerms"
                    placeholder="Select payment terms"
                  >
                    {getCustomerPaymentTermsOptions().map((term) => (
                      <Select.Option
                        key={`select-payment-term-${term.value}`}
                        value={term.value || ''}
                      >
                        {term.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="customerCostingMethodType"
                  label="Customer costing method type"
                >
                  <Select
                    name="customerCostingMethodType"
                    placeholder="Select customer costing method type"
                  >
                    <Select.Option
                      value={CustomerCostingMethodTypeEnum.Blended}
                    >
                      {getCustomerCostingMethodTypeLabel(
                        CustomerCostingMethodTypeEnum.Blended,
                      )}
                    </Select.Option>
                    <Select.Option
                      value={CustomerCostingMethodTypeEnum.CostPlus}
                    >
                      {getCustomerCostingMethodTypeLabel(
                        CustomerCostingMethodTypeEnum.CostPlus,
                      )}
                    </Select.Option>
                  </Select>
                </FormItem>
              </Col>
            </Row>

            <div className="modal-title">Rate overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="dtBillRateMultiplier"
                  label="DT bill rate multiplier"
                  data-id="editCustomerInfoDtBillRateMultiplierField"
                >
                  <InputNumber
                    name="dtBillRateMultiplier"
                    inputPrefix="$"
                    placeholder="0.00"
                    precision={2}
                  />
                </FormItem>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  name="markupRate"
                  label="Fixed markup rate"
                  data-id="editCustomerInfoMarkupRateField"
                >
                  <Input
                    name="markupRate"
                    placeholder="Enter fixed markup rate"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="otRateMultiplier"
                  label="OT Rate multiplier"
                  data-id="editCustomerInfoOtRateMultiplierField"
                >
                  <Input
                    name="otRateMultiplier"
                    placeholder="Enter OT Rate multiplier"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <div className="upload-wrapper">
                  <FormItem
                    name="conversion"
                    label="Temp-to-perm conversion"
                    data-id="editCustomerConversionField"
                    showValidateError={false}
                  >
                    <TempToPermConversionUploader
                      fieldName="conversion"
                      file={tempToPermConversionFileInfo}
                    />
                  </FormItem>
                </div>
              </Col>
            </Row>

            <div className="modal-title">MSP/VMS overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="MSPFee"
                  label="MSP fee"
                  data-id="editCustomerMSPFeeField"
                >
                  <InputNumber
                    name="MSPFee"
                    placeholder="Enter MSP fee %"
                    precision={2}
                    emptyValueAsString
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="VMSFee"
                  label="VMS fee"
                  data-id="editCustomerVMSFeeField"
                >
                  <InputNumber
                    name="VMSFee"
                    placeholder="Enter VMS fee %"
                    precision={2}
                    emptyValueAsString
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingInfo"
                  label="Invoicing information"
                  data-id="editCustomerInvoicingInfoField"
                >
                  <Input
                    name="invoicingInfo"
                    placeholder="Enter invoicing information"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingInstructions"
                  label="Special invoicing instructions"
                  data-id="editCustomerInvoicingInstructionsField"
                >
                  <Input
                    name="invoicingInstructions"
                    placeholder="Enter special invoicing instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingCycle"
                  label="Invoicing cycle"
                  data-id="editCustomerInvoicingCycleField"
                >
                  <Select
                    allowClear
                    name="invoicingCycle"
                    placeholder="Select invoicing cycle"
                  >
                    {getCustomerInvoicingCycleOptions().map(
                      (invoicingCycle) => (
                        <Select.Option
                          key={`select-invoicing-cycle-${invoicingCycle.value}`}
                          value={invoicingCycle.value || ''}
                        >
                          {invoicingCycle.label}
                        </Select.Option>
                      ),
                    )}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="payrollCycle"
                  label="Pay period"
                  data-id="editCustomerPayrollCycleField"
                >
                  <Select
                    allowClear
                    name="payrollCycle"
                    placeholder="Select pay period"
                  >
                    {getCustomerPayrollCycleOptions().map((payrollCycle) => (
                      <Select.Option
                        key={`select-payroll-cycle-${payrollCycle.value}`}
                        value={payrollCycle.value || ''}
                      >
                        {payrollCycle.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="isThirdWorkforce"
                  label="Is there a third party workforce?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isThirdWorkforce"
                    onChange={(ev) => handleIsThirdWorkforce(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              {formik.values.isThirdWorkforce && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem name="url" label="URL">
                      <Input
                        name="url"
                        placeholder="Enter URL"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={12}>
                    <FormItem name="credentials" label="Credentials">
                      <Input
                        name="credentials"
                        placeholder="Enter credentials"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem name="providerName" label="MSP/VMS provider name">
                      <Input
                        name="providerName"
                        placeholder="Enter MSP/VMS provider name"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                </>
              )}

              <Col xs={24} md={12}>
                <FormItem
                  name="purchaseOrder"
                  label="Purchase order"
                  data-id="editCustomerPurchaseOrderField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="purchaseOrder"
                    onChange={(ev) => handlePurchaseOrderChange(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              {formik.values.purchaseOrder && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="purchaseOrderInstructions"
                      label="Purchase order instructions"
                    >
                      <Input
                        name="purchaseOrderInstructions"
                        placeholder="Enter purchase order instructions"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="upload-wrapper">
                      <FormItem
                        name="purchaseOrderFileKey"
                        label="Purchase order"
                        data-id="editCustomerPurchaseOrderFileFileField"
                        showValidateError={false}
                      >
                        <PurchaseOrderUploader
                          fieldName="purchaseOrderFileKey"
                          file={purchaseOrderFile}
                        />
                      </FormItem>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
}
